import { type Dayjs } from 'dayjs';
import { type TApprovalLevel } from 'features/approval-template/types';
import {
  type ASSIGNEE_PERMISSION,
  type ASSIGNEE_TYPE,
  type TAssignee,
} from 'features/assignee/types';
import {
  type ListQueryResponse,
  type TPaginationQueryParams,
} from 'lib/rtkQuery/types';
import { type TCommentListItem } from 'shared/components/CommentsWidget/types';

export type ProposalState = {
  proposalDetail: TProposalDetail | null;
  isFetchingProposalDetail: boolean;
  proposalTypes: TProposalTypeListItem[] | null;
  isFetchingProposalTypes: boolean;
  proposalComments: TCommentListItem[] | null;
};

export enum PROPOSAL_STATUS {
  APPROVAL = 'APPROVAL', // ĐÃ PHÊ DUYỆT
  PENDING = 'PENDING', // CHỜ PHÊ DUYỆT
  REJECT = 'REJECT', // TỪ CHỐI
}

export enum PROPOSAL_TYPE {
  RETIMEKEEPING = 'RETIMEKEEPING',
  SICK = 'SICK',
  LEAVE = 'LEAVE',
  MEETING = 'MEETING',
  VEHICLE = 'VEHICLE',
  SICK_OTHER = 'SICK_OTHER',
  STATIONERY = 'STATIONERY',
  TIMEKEEPER = 'TIMEKEEPER',
  LEAVE_ANNUAL = 'LEAVE_ANNUAL',
  BUSINESS_TRIP = 'BUSINESS_TRIP',
  LEAVE_ABNORMAL = 'LEAVE_ABNORMAL',
  EVALUATE_LEADER = 'EVALUATE_LEADER',
  EVALUATE_MEMBER = 'EVALUATE_MEMBER',
  LEAVE_NO_SALARY = 'LEAVE_NO_SALARY',
  OVERTIME_DAYOFF = 'OVERTIME_DAYOFF',
  OVERTIME_NORMAL = 'OVERTIME_NORMAL',
  PROPOSAL_SALARY = 'PROPOSAL_SALARY',
  OVERTIME_HOLIDAY = 'OVERTIME_HOLIDAY',
  OVERTIME_CHAIRMAN = 'OVERTIME_CHAIRMAN',
}

export enum PROPOSAL_PERMISSION {
  CREATE = 'CREATE',
  VIEW = 'VIEW',
  PIC = 'PIC',
}

export enum PROPOSAL_SHIFT_TYPE {
  MORNING = 'morning',
  AFTERNOON = 'afternoon',
  ALL_DAY = 'allDay',
  SKIP = 'skip',
}

export enum ABNORMAL_LEAVE_TYPE {
  NORMAL = 'normal',
  SPECIAL = 'special',
  WOMEN = 'women',
  CHILD_CARE = 'child_care',
  WORK_REASON = 'work_reason',
  BY_REGIME = 'by_regime',
}

export type TGetCurrentUserProposalsQueryResponse = ListQueryResponse<
  TCurrentUserProposalListItem[]
>;

export type TCurrentUserProposalListItem = {
  id: number;
  proposal_code: string;
  create_user: {
    user_name: string;
    name_lowercase: string | null;
    image_id: string | null;
  };
  created_at: Dayjs;
  proposal_status: PROPOSAL_STATUS;
  proposal_type: PROPOSAL_TYPE;
  proposal_type_name: string;
  title: string;
  proposal_type_is_deleted: boolean;
};

export type TGetCurrentUserProposalsQueryParams = TPaginationQueryParams & {
  keyword?: string;
  type?: PROPOSAL_TYPE;
  proposal_status?: PROPOSAL_STATUS;
  proposal_permission?: PROPOSAL_PERMISSION;
  create_user?: string;
  created_at?: string;
  from_date?: string; // ISO8601 format
  to_date?: string; // ISO8601 format
};

export type TProposalTypeListItem = {
  id: string;
  name: string;
  created_at: string | null;
  content_key: string | null;
  type: string | null;
  is_deleted: boolean;
  group_name: string;
  approval_template_code: string | null;
  approval_template: TApprovalLevel | null;
};

export type TGetProposalTypesQueryParams = Partial<TPaginationQueryParams> & {
  keyword?: string;
};

export type TProposalTimeListFormRow = {
  date: string; // ISO timestamp
  shift: PROPOSAL_SHIFT_TYPE;
  mode?: ABNORMAL_LEAVE_TYPE | null;
  mode_type?: number | null;
  note?: string | null;
};

export type TProposalTimeListPayloadItem = TProposalTimeListFormRow & {
  key: string;
};

export type TApprovalLevelPayload = {
  level: number;
  name: string;
  username: string;
  allow_edit: boolean;
  status: APPROVAL_LEVEL_STATUS;
};

export type TCreateProposalPayload = {
  proposal_type: PROPOSAL_TYPE;
  title: string;
  reason: string;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  attachments?: string[];
  assignees?: TAssignee[];
  approval_levels: TApprovalLevelPayload[];
  proposal_time_list?: TProposalTimeListPayloadItem[];
  reason_for_leaving?: string | null;
  overtime?: string;
};

export type TUpdateProposalAttachments = {
  remove_items: string[];
  new_items: string[];
};

export type TUpdateProposalPayload = {
  code: string;
  proposal_code: string;
  proposal_type: PROPOSAL_TYPE;
  title: string;
  reason: string;
  from_date: string; // ISO8601
  to_date: string; // ISO8601
  attachments: TUpdateProposalAttachments;
  proposal_time_list: TProposalTimeListPayloadItem[];
};

export type TCreateProposalResponse = {
  id: number;
  proposal_code: string;
  proposal_number: string | null;
  reason: string | null;
  create_user: {
    user_name: string;
    name_lowercase: string | null;
    image_id: string | null;
  } | null;
  created_at: string;
  type_join: string | null;
  proposal_status: PROPOSAL_STATUS;
  proposal_type: PROPOSAL_TYPE;
  proposal_type_name: string | null;
  proposal_type_is_deleted: boolean | null;
  title: string;
  has_no_config_approval_levels: boolean;
  assignees: TAssignee[] | null;
  approval_levels: TProposalApprovalLevel[] | null;
  attachments: unknown[] | null;
  reason_for_leaving: string | null;
  type_of_leave: string | null;
  from_date: string | null;
  to_date: string | null;
  overtime: string | null;
  approval_overtime: string | null;
  request: string | null;
  proposal_time_list: TProposalTimeList[] | null;
};

export enum APPROVAL_LEVEL_STATUS {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  WAITING = 'WAITING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum APPROVAL_ACTION_STATUS {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/** Approve / reject the current user's approval level on a proposal. */
export type TApproveProposalPayload = {
  proposalCode: string;
  status: APPROVAL_ACTION_STATUS;
  comment: string;
};

export type TProposalApprovalLevel = {
  code: string;
  name: string;
  level: number;
  allow_edit: boolean;
  username: string;
  status: APPROVAL_LEVEL_STATUS;
  comment: string | null;
  created_at: string; // ISO8601 format
  updated_at: string; // ISO8601 format
};

export type TProposalTimeList = {
  date: string;
  id: number;
  mode: ABNORMAL_LEAVE_TYPE;
  mode_type: number | null;
  note: string | null;
  proposal_code: string;
  proposal_id: string;
  shift: PROPOSAL_SHIFT_TYPE;
};

export type TProposalDetail = {
  id: number;
  proposal_code: string;
  proposal_number: string | null;
  reason: string;
  create_user: {
    user_name: string;
    name_lowercase: string | null;
    image_id: string | null;
  };
  created_at: string; // ISOString format
  type_join: string | null;
  proposal_status: PROPOSAL_STATUS;
  proposal_type: PROPOSAL_TYPE;
  proposal_type_name: string | null;
  proposal_type_is_deleted: boolean | null;
  title: string;
  has_no_config_approval_levels: boolean;
  assignees: TAssignee[];
  approval_levels: TProposalApprovalLevel[];
  attachments: unknown[];
  reason_for_leaving: string | null;
  type_of_leave: string | null;
  from_date: string; // YYYY-MM-DD
  to_date: string; // YYYY-MM-DD
  overtime: string | null;
  approval_overtime: string | null;
  request: string | null;
  proposal_time_list: TProposalTimeList[];
};

export type TFollowerAssigneeItem = {
  assignee_code: string;
  assignee_type: ASSIGNEE_TYPE.USER;
  permission: ASSIGNEE_PERMISSION.VIEW;
};

export type TUpdateProposalFollowersPayload = {
  code: string;
  assignees: {
    new_items: TFollowerAssigneeItem[];
    remove_items: TFollowerAssigneeItem[];
  };
};

export type TDeleteProposalCommentPayload = {
  proposalCode: string;
  commentId: string;
};

export type TCreateProposalCommentPayload = {
  proposalCode: string;
  data: {
    content: string;
    file_ids: string[];
  };
};

export type TCreateProposalTypePayload = {
  name: string;
  content_key: string | null;
  approval_template_code?: string;
};

export type TUpdateProposalTypePayload = TCreateProposalTypePayload & {
  id: string;
};
